import { Button, Icon, Stack, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { ArrowNarrowRightIcon } from '@heroicons/react/outline';
import { useTranslations } from 'use-intl';

import { ISubscription, noop } from '@blockpulse3/data/shared';
import { IdentityType } from '@blockpulse3/graphql/hooks';
import { formatDate, formatNumberCurrency } from '@blockpulse3/helpers';
import { CompanyIdentityAvatar, IdentityAvatar, TableContainer } from '@blockpulse3/ui/commons';

import { InvitationsTableEmpty } from './InvitationsTableEmpty';

type Props = {
  /* ** On row click ** */
  onInvitationClick?: (subscription: ISubscription, subCompanyId?: string) => void;
  /* ** Table values ** */
  values: ISubscription[];
};

export function InvitationsTable({ onInvitationClick = noop, values }: Props): JSX.Element {
  const i18nInvitationsTable = useTranslations('Invitations.Table');

  const isEmpty = !values.length;

  const handleSubscriptionClick = (subscription: ISubscription, subCompanyId?: string): void => {
    onInvitationClick(subscription, subCompanyId);
  };

  return isEmpty ? (
    <InvitationsTableEmpty />
  ) : (
    <TableContainer>
      <Table variant="striped">
        <Thead>
          <Tr>
            <Th width="fit-content" />
            <Th>{i18nInvitationsTable('Operations')}</Th>
            <Th>{i18nInvitationsTable('CompanyName')}</Th>
            <Th>{i18nInvitationsTable('SubscriberName')}</Th>
            <Th isNumeric>{i18nInvitationsTable('Amount')}</Th>
            <Th>{i18nInvitationsTable('EndDate')}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {values.map((subscription) => {
            const isSubscriberCompany = subscription.identity.type === IdentityType.COMPANY;
            return (
              <Tr
                key={subscription.id}
                role="button"
                onClick={(): void =>
                  handleSubscriptionClick(
                    subscription,
                    isSubscriberCompany ? subscription.identity?.companyIdentity?.id : undefined,
                  )
                }
              >
                <Td textAlign="right" width="1%">
                  <Button
                    rightIcon={<Icon as={ArrowNarrowRightIcon} boxSize="5" />}
                    variant="secondary"
                  >
                    {i18nInvitationsTable('AccessInvitation')}
                  </Button>
                </Td>
                <Td>
                  <Text fontWeight="600">{subscription.fundraising.name}</Text>
                  <Text color="gray.500" fontWeight="400">
                    {i18nInvitationsTable('CapitalIncrease')}
                  </Text>
                </Td>
                <Td fontWeight="600">
                  <Stack alignItems="center" direction="row">
                    <CompanyIdentityAvatar
                      src={subscription.fundraising.company.identity?.profilePicture}
                    />
                    <Text>{subscription.fundraising.company.name}</Text>
                  </Stack>
                </Td>
                <Td fontWeight="600">
                  <Stack alignItems="center" direction="row">
                    <IdentityAvatar identity={subscription.identity} />
                    <Text>
                      {isSubscriberCompany
                        ? subscription.identity?.companyIdentity?.name
                        : subscription.identity?.individualIdentity?.name}
                    </Text>
                  </Stack>
                </Td>
                <Td isNumeric fontWeight="600">
                  {subscription.investAmount === 0
                    ? '-'
                    : formatNumberCurrency(subscription.investAmount)}
                </Td>
                <Td fontWeight="600">
                  {formatDate(subscription.fundraising.endSubscriptionDate, 'D MMMM YYYY')}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
}

export type FundraisingTableProps = Props;
